import {type JSX, useEffect, useState} from 'react'

import {BackendAPI, type SvgDrawingGenerationRequest} from '../api/BackendAPI'
import {useAppSelector} from '../hooks'
import type {PlanState, SmokeDetectorType, YesNo} from '../reducers/types'
import type {RootState} from '../store'

interface DrawingProps {
  enableUpdating: boolean
}

/**
 * SVG drawing component for the design plan.
 */
export default function Drawing({enableUpdating}: Readonly<DrawingProps>): JSX.Element | null {
  const plan = useAppSelector<PlanState>((state: RootState) => state.plan)
  const token = useAppSelector((state: RootState) => state.auth.token)
  const [uri, setUri] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (enableUpdating) {
      const drawingInput = mapPlanToDrawingParams(plan)
      if (token) {
        updateSvgGraph(drawingInput, token, setUri)
      }
    }
  }, [enableUpdating, plan, token])

  if (plan.apartmentQuantity === undefined) {
    return null
  }

  if (!uri) {
    // note: returning 'null' here is important so that the svg graph update call change is detected correctly
    // (when loading the svg has finished)
    return null
  }

  return (
    <embed aria-description='Preview schematic' src={uri}/>
  )
}

/* Helper Functions */

function mapPlanToDrawingParams(plan: PlanState): SvgDrawingGenerationRequest {
  const wireless = plan.systemType === 'wireless'
  const temperatureMetersEnabled = (wireless && plan.temperatureMetersEnabled === 'yes')

  return {
    systemType: plan.systemType ? plan.systemType : 'wired',
    wirelessMode: plan.wirelessMode ? plan.wirelessMode : undefined,
    buildingType: plan.buildingType ? plan.buildingType : 'block',
    waterMeterType: plan.waterMeterType ? plan.waterMeterType : 'cold',
    apartmentScreensEnabled: toYesNo(plan.apartmentScreensEnabled, 'no'),
    moreThanOneMeterPerApartment: toYesNo(plan.moreThanOneMeterPerApartment, 'no'),
    temperatureMetersEnabled: booleanToYesNo(temperatureMetersEnabled),
    smokeDetectorType: resolveSmokeDetectorType(plan),
    apartmentQuantity: plan.apartmentQuantity ?? 1,
    floorQuantity: plan.floorQuantity ?? 1,
    stairwellQuantity: plan.stairwellQuantity ?? 1
  }
}

function resolveSmokeDetectorType(plan: PlanState): Exclude<SmokeDetectorType, 'none'> | undefined {
  const hydrolinkAirSelected = plan.systemType === 'wireless' && plan.wirelessMode === 'hydrolinkAir'
  if (!hydrolinkAirSelected || plan.smokeDetectorType === 'none') {
    return undefined
  }

  return plan.smokeDetectorType
}

function toYesNo(value: "" | YesNo, defaultValue: YesNo): YesNo {
  return value || defaultValue
}

function booleanToYesNo(value: boolean): YesNo {
  // eslint-disable-next-line sonarjs/no-selector-parameter -- this is mapper func (not a selector)
  return value ? 'yes' : 'no'
}

async function updateSvgGraph(
  input: SvgDrawingGenerationRequest,
  token: string,
  setUri: (uri: string | undefined) => void) {
  const res = await BackendAPI.getSvgDrawing(input, token)
  if (res.succeeded) {
    setUri(`data:image/svg+xml;base64,${res.payload.svg}`)
  } else {
    setUri(undefined)
  }
}
